import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Reports.css';

interface SystemOffer {
  id: number;
  offerName: string;
  advertiser: string;
  category: string;
  eventType: 'install' | 'lead' | 'sale' | 'event';
  systemPayout: number;
  affiliatePayout: number;
  margin: number;
  marginPercent: number;
  countries: string[];
  devices: string[];
  operatingSystem: string[];
  allowedTrafficSources: string[];
  restrictions: string[];
  caps: {
    daily?: number;
    weekly?: number;
    monthly?: number;
  };
  targeting: {
    geoWhitelist: string[];
    geoBlacklist: string[];
    deviceWhitelist: string[];
    deviceBlacklist: string[];
    osWhitelist: string[];
    osBlacklist: string[];
  };
  autoApproval: boolean;
  requiresApproval: boolean;
  featured: boolean;
  priority: number;
  status: 'active' | 'paused' | 'disabled' | 'testing';
  createdDate: string;
  lastModified: string;
  createdBy: string;
  performance: {
    impressions: number;
    clicks: number;
    conversions: number;
    revenue: number;
    ctr: number;
    cvr: number;
    epc: number;
  };
}

const SystemOffers: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('priority');
  const [showAddForm, setShowAddForm] = useState(false);
  const [systemOffers, setSystemOffers] = useState<SystemOffer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSystemOffers = async () => {
      try {
        setLoading(true);
        const response = await apiService.getOffers();
        const offers = response.offers || [];

        // Transform regular offers into system offers with calculated metrics
        const transformedOffers: SystemOffer[] = offers.map((offer: any, index: number) => {
          const payout = parseFloat(offer.payout || 0);
          const affiliatePayout = payout * 0.8; // 80% goes to affiliate
          const systemPayout = payout;
          const margin = systemPayout - affiliatePayout;
          const marginPercent = systemPayout > 0 ? (margin / systemPayout * 100) : 0;

          return {
            id: offer.id,
            offerName: offer.name,
            advertiser: offer.advertiser?.name || 'Unknown',
            category: 'General',
            eventType: offer.goal_type || 'install',
            systemPayout,
            affiliatePayout,
            margin,
            marginPercent,
            countries: [],
            devices: [],
            operatingSystem: [],
            allowedTrafficSources: [],
            restrictions: [],
            caps: {
              daily: offer.daily_cap || undefined
            },
            targeting: {
              geoWhitelist: [],
              geoBlacklist: [],
              deviceWhitelist: [],
              deviceBlacklist: [],
              osWhitelist: [],
              osBlacklist: []
            },
            autoApproval: !offer.is_test,
            requiresApproval: offer.is_test,
            featured: false,
            priority: index + 1,
            status: offer.is_test ? 'testing' : (offer.is_active ? 'active' : 'disabled'),
            createdDate: new Date(offer.created_at).toISOString().split('T')[0],
            lastModified: new Date(offer.updated_at || offer.created_at).toISOString().split('T')[0],
            createdBy: 'admin@adbizz.com',
            performance: {
              impressions: offer.total_impressions || 0,
              clicks: offer.total_clicks || 0,
              conversions: offer.total_conversions || 0,
              revenue: (offer.total_conversions || 0) * payout,
              ctr: offer.total_impressions > 0 ? ((offer.total_clicks || 0) / offer.total_impressions * 100) : 0,
              cvr: offer.total_clicks > 0 ? ((offer.total_conversions || 0) / offer.total_clicks * 100) : 0,
              epc: offer.total_clicks > 0 ? ((offer.total_conversions || 0) * payout / offer.total_clicks) : 0
            }
          };
        });

        setSystemOffers(transformedOffers);
      } catch (error: any) {
        console.error('Failed to fetch system offers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemOffers();
  }, []);

  const filteredOffers = systemOffers.filter(offer => {
    const searchMatch = offer.offerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       offer.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       offer.category.toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch = statusFilter === 'all' || offer.status === statusFilter;
    const categoryMatch = categoryFilter === 'all' || offer.category.toLowerCase().includes(categoryFilter.toLowerCase());
    const eventMatch = eventFilter === 'all' || offer.eventType === eventFilter;

    return searchMatch && statusMatch && categoryMatch && eventMatch;
  });

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        return a.priority - b.priority;
      case 'margin':
        return b.margin - a.margin;
      case 'marginPercent':
        return b.marginPercent - a.marginPercent;
      case 'conversions':
        return b.performance.conversions - a.performance.conversions;
      case 'revenue':
        return b.performance.revenue - a.performance.revenue;
      case 'epc':
        return b.performance.epc - a.performance.epc;
      default:
        return a.priority - b.priority;
    }
  });

  const calculateStats = () => {
    return {
      total: filteredOffers.length,
      active: filteredOffers.filter(o => o.status === 'active').length,
      testing: filteredOffers.filter(o => o.status === 'testing').length,
      paused: filteredOffers.filter(o => o.status === 'paused').length,
      disabled: filteredOffers.filter(o => o.status === 'disabled').length,
      featured: filteredOffers.filter(o => o.featured).length,
      autoApproval: filteredOffers.filter(o => o.autoApproval).length,
      totalRevenue: filteredOffers.reduce((sum, o) => sum + o.performance.revenue, 0),
      totalMargin: filteredOffers.reduce((sum, o) => sum + (o.performance.conversions * o.margin), 0),
      avgMarginPercent: filteredOffers.reduce((sum, o) => sum + o.marginPercent, 0) / filteredOffers.length
    };
  };

  const stats = calculateStats();

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'testing': return 'info';
      case 'paused': return 'warning';
      case 'disabled': return 'danger';
      default: return 'secondary';
    }
  };

  const getEventTypeClass = (eventType: string) => {
    switch (eventType) {
      case 'install': return 'install';
      case 'lead': return 'lead';
      case 'sale': return 'sale';
      case 'event': return 'event';
      default: return 'default';
    }
  };

  const getPriorityClass = (priority: number) => {
    switch (priority) {
      case 1: return 'danger';
      case 2: return 'warning';
      case 3: return 'info';
      default: return 'secondary';
    }
  };

  const toggleStatus = (id: number) => {
    console.log(`Toggling status for system offer ${id}`);
  };

  const toggleFeatured = (id: number) => {
    console.log(`Toggling featured status for system offer ${id}`);
  };

  const exportData = (format: string) => {
    console.log(`Exporting system offers as ${format}`);
    alert(`Exporting ${filteredOffers.length} system offers as ${format}`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>System Offers</h1>
        <div className="header-actions">
          <button
            className="btn btn-success"
            onClick={() => setShowAddForm(true)}
          >
            Add System Offer
          </button>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Offers</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="summary-card">
          <h3>Active</h3>
          <div className="value approved">{stats.active}</div>
        </div>
        <div className="summary-card">
          <h3>Testing</h3>
          <div className="value">{stats.testing}</div>
        </div>
        <div className="summary-card">
          <h3>Featured</h3>
          <div className="value pending">{stats.featured}</div>
        </div>
        <div className="summary-card">
          <h3>Auto Approval</h3>
          <div className="value">{stats.autoApproval}</div>
        </div>
        <div className="summary-card">
          <h3>Total Revenue</h3>
          <div className="value">${stats.totalRevenue.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <h3>Total Margin</h3>
          <div className="value">${stats.totalMargin.toFixed(2)}</div>
        </div>
        <div className="summary-card">
          <h3>Avg Margin %</h3>
          <div className="value">{stats.avgMarginPercent.toFixed(1)}%</div>
        </div>
      </div>

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="testing">Testing</option>
              <option value="paused">Paused</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Categories</option>
              <option value="social">Social Media</option>
              <option value="gaming">Gaming</option>
              <option value="finance">Finance</option>
              <option value="ecommerce">E-commerce</option>
              <option value="education">Education</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Event Type:</label>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="event-select"
            >
              <option value="all">All Events</option>
              <option value="install">Install</option>
              <option value="lead">Lead</option>
              <option value="sale">Sale</option>
              <option value="event">Custom Event</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="group-select"
            >
              <option value="priority">Priority</option>
              <option value="margin">Margin</option>
              <option value="marginPercent">Margin %</option>
              <option value="conversions">Conversions</option>
              <option value="revenue">Revenue</option>
              <option value="epc">EPC</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search offers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="export-buttons">
          <button className="btn btn-success" onClick={() => exportData('CSV')}>
            CSV
          </button>
          <button className="btn btn-warning" onClick={() => exportData('Excel')}>
            Excel
          </button>
          <button className="btn btn-danger" onClick={() => exportData('PDF')}>
            PDF
          </button>
        </div>
      </div>

      {/* System Offers Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Offer</th>
                <th>Advertiser</th>
                <th>Category</th>
                <th>Event Type</th>
                <th>System Payout</th>
                <th>Affiliate Payout</th>
                <th>Margin</th>
                <th>Margin %</th>
                <th>Performance</th>
                <th>Caps</th>
                <th>Targeting</th>
                <th>Approval</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedOffers.map((offer) => (
                <tr key={offer.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`status-badge ${getPriorityClass(offer.priority)}`}>
                        P{offer.priority}
                      </span>
                      {offer.featured && (
                        <span style={{ fontSize: '16px', color: '#f39c12' }}>⭐</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="offer-cell">
                      <span className="offer-name">{offer.offerName}</span>
                      <span className="offer-id">ID: {offer.id}</span>
                    </div>
                  </td>
                  <td>{offer.advertiser}</td>
                  <td>{offer.category}</td>
                  <td>
                    <span className={`event-type ${getEventTypeClass(offer.eventType)}`}>
                      {offer.eventType.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className="revenue">${offer.systemPayout.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="payout">${offer.affiliatePayout.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="profit">${offer.margin.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="profit">{offer.marginPercent.toFixed(1)}%</span>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                      <div>Conv: {offer.performance.conversions.toLocaleString()}</div>
                      <div>CVR: {offer.performance.cvr.toFixed(1)}%</div>
                      <div>EPC: ${offer.performance.epc.toFixed(2)}</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                      <div>Daily: {offer.caps.daily || 'No limit'}</div>
                      <div>Weekly: {offer.caps.weekly || 'No limit'}</div>
                      <div>Monthly: {offer.caps.monthly || 'No limit'}</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                      <div>Countries: {offer.countries.length}</div>
                      <div>Devices: {offer.devices.length}</div>
                      <div>OS: {offer.operatingSystem.length}</div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '12px' }}>
                      <div className={offer.autoApproval ? 'profit' : 'pending'}>
                        {offer.autoApproval ? 'Auto' : 'Manual'}
                      </div>
                      <div className={offer.requiresApproval ? 'pending' : 'profit'}>
                        {offer.requiresApproval ? 'Required' : 'Not Required'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(offer.status)}`}>
                      {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-info btn-sm">View</button>
                      <button className="btn btn-warning btn-sm">Edit</button>
                      <button
                        className={`btn btn-sm ${offer.featured ? 'btn-secondary' : 'btn-success'}`}
                        onClick={() => toggleFeatured(offer.id)}
                      >
                        {offer.featured ? 'Unfeature' : 'Feature'}
                      </button>
                      <button
                        className={`btn btn-sm ${offer.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                        onClick={() => toggleStatus(offer.id)}
                      >
                        {offer.status === 'active' ? 'Pause' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredOffers.length} system offers
        </div>
      </div>
    </div>
  );
};

export default SystemOffers;